import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useHandleForum } from "../../../../core/hooks/useHandleForum";
import { Link, useParams } from "react-router-dom";
import { CustomEditor } from "../../../../core/common/ults/editor";
import Skeleton from "react-loading-skeleton";
import { convertTimestampToDate } from "../../../../core/common/ults/dateTime";
import { DiscussionModal } from "../../../Courses/course-details/modal";
import { useTranslation } from "react-i18next";

const ForumDetailTable = () => {
  const { id } = useParams();
  const forum = useSelector((state: any) => state.forumSlice.forum);
  const { forumDisscusion, isForumDisscusionLoading, createDiscussion, refetchDiscussions } = useHandleForum(id);
  const [showDiscussionModal, setShowDiscussionModal] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [showCreateDiscussion, setShowCreateDiscussion] = useState(false);
  const { t } = useTranslation();

  const initForumForm = {
    forumid: id?.toString(),
    subject: "",
    message: "",
  };
  const [formData, setFormData] = useState(initForumForm);

  const handleEditorChange = (content: string, editor: any) => {
    const name = editor.id;
    setFormData((prevState) => ({
      ...prevState,
      [name]: content,
    }));
  };

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCancleInput = () => {
    setFormData(initForumForm);
    setShowCreateDiscussion(false);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    createDiscussion(formData);
    setFormData(initForumForm);
    setShowCreateDiscussion(false);
  };

  const handleSelectDiscussion = (discussion: any) => {
    setSelectedDiscussion(discussion);
    setShowDiscussionModal(true);
  };

  const handleCloseDiscussionModal = () => {
    setShowDiscussionModal(false);
    setSelectedDiscussion(null);
  };

  return (
    <div className="forum-table-wrapper mt-4">
      <h6 className="mb-4">{forum?.forum_name}</h6>
      <button
        className="btn btn-outline-secondary mb-4"
        onClick={() => setShowCreateDiscussion(true)}
      >
        {t("student.forums.detailTable.addNewTopic")}
      </button>
      <div className="overflow-x-auto">
        <table className="w-100 border rounded-lg text-sm">
          <thead>
            <tr className="bg-blue-700 text-white">
              <th className="py-2 px-4 font-semibold text-center text-black border bg-blue w-40">
                {t("student.forums.detailTable.discussion")}
              </th>
              <th className="py-2 px-4 font-semibold text-center text-black border bg-blue w-25 ">
                {t("student.forums.detailTable.creator")}
                <span className="text-xs">↓</span>
              </th>
              <th className="py-2 px-4 font-semibold text-center text-black border bg-blue w-25 ">
                {t("student.forums.detailTable.latestInteraction")}
              </th>
              <th className="py-2 px-4 font-semibold text-center text-black border bg-blue ">
                {t("student.forums.detailTable.replies")}
              </th>
            </tr>
          </thead>
          {isForumDisscusionLoading ? (
            <tbody>
              <Skeleton height={100} className="mb-3" />
            </tbody>
          ) : (
            <>
              {forumDisscusion?.discussions?.map((item: any, idx: any) => (
                <tbody key={idx}>
                  <tr className="border-b">
                    <td className="py-3 px-4 w-25 discussion-name" onClick={() => handleSelectDiscussion(item)}>
                      <span className="font-medium d-flex justify-content-start">
                        {item?.name}
                      </span>
                    </td>
                    <td className="py-3 px-4 w-25">
                      <div className="flex items-center flex-row">
                        <div className="d-flex flex-column align-items-center gap-1">
                          <span className="block">
                            {item?.userfullname}
                          </span>
                          <span className="block">
                            {convertTimestampToDate(item?.created)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 w-25">
                      <div className="flex items-center flex-row">
                        <div className="d-flex flex-column align-items-center gap-1">
                          <span className="block">
                            {item?.usermodifiedfullname}
                          </span>
                          <span className="block">
                            {convertTimestampToDate(item?.timemodified)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center w-25">
                      {item?.numreplies}
                    </td>
                  </tr>
                </tbody>
              ))}
            </>
          )}
        </table>
      </div>

      {/* modal create new discussion */}
      {showCreateDiscussion && (
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5>{t("student.forums.detailTable.addDiscussion")}</h5>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  onClick={() => setShowCreateDiscussion(false)}
                  aria-label="Close"
                >
                  <i className="isax isax-close-circle5" />
                </button>
              </div>
              <div className="modal-body">
                <form className="space-y-6" onSubmit={(e) => handleSubmit(e)}>
                  <div className="d-flex items-start gap-5 mb-3">
                    <label className="w-24 pt-2 text-sm font-medium text-gray-700 flex-shrink-0">
                      {t("student.forums.detailTable.title")}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="flex-1">
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={t("student.forums.detailTable.enterTitle")}
                        required
                        name="subject"
                        value={formData?.subject || ""}
                        onChange={handleInputChange}
                        maxLength={255}
                      />
                    </div>
                  </div>
                  <div className="d-flex w-100 items-start gap-5">
                    <label className="w-24 pt-2 text-sm font-medium text-gray-700 flex-shrink-0">
                      {t("student.forums.detailTable.content")}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="flex-1">
                      <CustomEditor
                        value={formData?.message || ""}
                        onEditorChange={handleEditorChange}
                        id="message"
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-end gap-2 pt-4">
                    <button type="submit" className="btn btn-primary">
                      {t("student.forums.detailTable.postToForum")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleCancleInput}
                    >
                      {t("student.forums.detailTable.cancel")}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </div>
      )}

      {/* modal view discussion detail */}
      {showDiscussionModal && (
        <DiscussionModal
          selectedDiscussion={selectedDiscussion}
          onClose={handleCloseDiscussionModal}
          forumId={id}
          refetchDiscussions={refetchDiscussions}
        />
      )}
    </div>
  );
};

export default ForumDetailTable;
